import React from 'react';
import { Helmet } from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';

export type SEOProps = {
  title: string;
  description: string;
  meta?: [
    {
      name?: string;
      property?: string;
      content: string;
    },
  ];
};

type PureSEOProps = SEOProps & {
  sharingImage: string;
};

const TITLE_FORMAT = (title: string) => `HP Dev One | ${title}`;

/**
 * SEO Componet for testing only.
 * Pure components work better than contextual components for tests:
 * https://www.gatsbyjs.com/docs/how-to/testing/testing-components-with-graphql/
 */
export const PureSEO: React.FC<PureSEOProps> = ({
  title,
  description,
  sharingImage,
  meta = [],
}) => (
  <Helmet
    title={title}
    titleTemplate={TITLE_FORMAT(title)}
    meta={[
      ...meta,
      { name: 'description', content: description },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: sharingImage },
    ]}
  />
);

/**
 * Contextual SEO component that uses StaticQuery
 * for site metadata.
 */
const SEO: React.FC<SEOProps> = ({ title, description, meta }) => {
  const { site, seoImage } =
    useStaticQuery<GatsbyTypes.SEOSiteInfoQuery>(graphql`
      query SEOSiteInfo {
        site {
          siteMetadata {
            description
            title
          }
        }
        seoImage: graphCmsSeoImage05 {
          seoImage {
            gatsbyImageData(height: 650, width: 1200)
          }
        }
      }
    `);
  const metaTitle = title ?? site?.siteMetadata?.title;
  const metaDescription = description ?? site?.siteMetadata?.description;
  const metaImage = seoImage?.seoImage?.gatsbyImageData?.images.fallback.src;

  return (
    <PureSEO
      title={metaTitle}
      description={metaDescription}
      sharingImage={metaImage}
      meta={meta}
    />
  );
};

export default SEO;
